import makerjs from 'makerjs'
import Decimal from 'decimal.js'

// gt.svg 中的形状元素（SVG 坐标，单位: point, 1mm = 2.83465pt）
const SCALE = 1 / 2.83465 // point -> mm

// 原始 SVG 所有元素的边界框中心（SVG 坐标）
// X: min≈304.29, max≈350.86 → CX≈327.575
// Y: min≈275.00, max≈292.08 → CY≈283.54
const CX = 327.575
const CY = 283.54

// 将 SVG 坐标转为以中心为原点的 mm 坐标（Y轴翻转）
function toMM(svgX, svgY) {
    return [(svgX - CX) * SCALE, -(svgY - CY) * SCALE]
}

/**
 * 生成单个底棉模型（以原点为中心）
 * 形状来源: public/gt.svg（真实路径）
 */
function generateSingleFoam() {
    const foam = { paths: {}, models: {} }

    // ========== 1. 左侧折线 ==========
    // polyline: 328.27 279.26 → 311.27 279.26 → 311.27 283.61 → 304.29 283.61 → 304.29 292.08 → 324 292.08
    const leftPts = [
        toMM(328.27, 279.26),
        toMM(311.27, 279.26),
        toMM(311.27, 283.61),
        toMM(304.29, 283.61),
        toMM(304.29, 292.08),
        toMM(324.00, 292.08),
    ]
    for (let i = 0; i < leftPts.length - 1; i++) {
        foam.paths['left_seg' + i] = new makerjs.paths.Line(leftPts[i], leftPts[i + 1])
    }

    // ========== 2. 右侧折线 ==========
    // polyline: 328.25 287.83 → 343.88 287.83 → 343.88 283.47 → 350.86 283.47 → 350.86 275 → 332.52 275
    const rightPts = [
        toMM(328.25, 287.83),
        toMM(343.88, 287.83),
        toMM(343.88, 283.47),
        toMM(350.86, 283.47),
        toMM(350.86, 275.00),
        toMM(332.52, 275.00),
    ]
    for (let i = 0; i < rightPts.length - 1; i++) {
        foam.paths['right_seg' + i] = new makerjs.paths.Line(rightPts[i], rightPts[i + 1])
    }

    // ========== 3. 左下圆弧 ==========
    // M332.52,275 c0,2.35 -1.9,4.25 -4.25,4.25
    // 从 (332.52,275) 到 (328.27,279.25)，半径 4.25 的四分之一圆
    {
        const p0 = toMM(332.52, 275)
        const c1 = toMM(332.52, 275 + 2.35)
        const c2 = toMM(332.52 - 1.9, 275 + 4.25)
        const p3 = toMM(332.52 - 4.25, 275 + 4.25) // ≈ (328.27, 279.25)
        foam.models['arc_left'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)
    }

    // ========== 4. 右上圆弧 ==========
    // M324,292.08 c0,-2.35 1.9,-4.25 4.25,-4.25
    // 从 (324,292.08) 到 (328.25,287.83)，半径 4.25 的四分之一圆
    {
        const p0 = toMM(324, 292.08)
        const c1 = toMM(324, 292.08 - 2.35)
        const c2 = toMM(324 + 1.9, 292.08 - 4.25)
        const p3 = toMM(324 + 4.25, 292.08 - 4.25) // ≈ (328.25, 287.83)
        foam.models['arc_right'] = new makerjs.models.BezierCurve(p0, c1, c2, p3)
    }

    return foam
}

/**
 * 为所有按键生成底棉布局
 * @param {Array} keysArray - KLE 解析后的 Key 对象数组
 * @param {Object} options - { unitWidth, unitHeight, extraRotation, boardRadius }
 * @returns {Object} makerjs 模型
 */
export function buildFoams(keysArray, options) {
    const {
        unitWidth = new Decimal(19.05),
        unitHeight = new Decimal(19.05),
        extraRotation = 0,  // 额外旋转角度（下灯位时传180）
        boardRadius = new Decimal(2),
    } = options

    const canvas = { models: {} }
    const singleFoam = generateSingleFoam()

    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    let id = 0
    for (const key of keysArray) {
        // 计算按键中心位置（mm）
        const originX = key.centerX.times(unitWidth).toNumber()
        const originY = key.centerY.times(unitHeight).times(-1).toNumber()

        // 克隆底棉模型
        const foamInstance = makerjs.model.clone(singleFoam)

        // 应用旋转：键自身旋转（取负）+ 额外旋转（灯位）
        const angle = key.angle.times(-1).toNumber() + extraRotation
        if (angle !== 0) {
            makerjs.model.rotate(foamInstance, angle)
        }

        // 偏移先于旋转：偏移向量随按键角度旋转
        const angleRad = angle * Math.PI / 180
        const offsetX = 0.62 * Math.cos(angleRad) - (-3.87) * Math.sin(angleRad)
        const offsetY = 0.62 * Math.sin(angleRad) + (-3.87) * Math.cos(angleRad)
        foamInstance.origin = [originX + offsetX, originY + offsetY]

        canvas.models['Foam_' + id] = foamInstance

        // 追踪边界（单位矩形旋转后的轴对齐包围盒）
        const halfW = key.width.times(unitWidth).times(0.5).toNumber()
        const halfH = key.height.times(unitHeight).times(0.5).toNumber()
        const rad = angle * Math.PI / 180
        const cosA = Math.cos(rad)
        const sinA = Math.sin(rad)
        const corners = [
            [originX + (-halfW * cosA - (-halfH) * sinA), originY + (-halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - (-halfH) * sinA), originY + (halfW * sinA + (-halfH) * cosA)],
            [originX + (halfW * cosA - halfH * sinA), originY + (halfW * sinA + halfH * cosA)],
            [originX + (-halfW * cosA - halfH * sinA), originY + (-halfW * sinA + halfH * cosA)],
        ]
        for (const [cx, cy] of corners) {
            if (cx < minX) minX = cx
            if (cx > maxX) maxX = cx
            if (cy < minY) minY = cy
            if (cy > maxY) maxY = cy
        }

        id++
    }

    // 添加整体边框（与定位板一致）
    if (id > 0) {
        const boardRadiusNum = boardRadius ? boardRadius.toNumber() : 0
        const r = Math.min(boardRadiusNum, (maxX - minX) / 2, (maxY - minY) / 2)

        if (r > 0) {
            // 圆角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    // 四条直线边
                    lineTop: new makerjs.paths.Line([minX + r, maxY], [maxX - r, maxY]),
                    lineBottom: new makerjs.paths.Line([minX + r, minY], [maxX - r, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY - r], [minX, minY + r]),
                    lineRight: new makerjs.paths.Line([maxX, maxY - r], [maxX, minY + r]),
                    // 四个圆角
                    arcTopRight: new makerjs.paths.Arc([maxX - r, maxY - r], r, 0, 90),
                    arcBottomRight: new makerjs.paths.Arc([maxX - r, minY + r], r, 270, 360),
                    arcBottomLeft: new makerjs.paths.Arc([minX + r, minY + r], r, 180, 270),
                    arcTopLeft: new makerjs.paths.Arc([minX + r, maxY - r], r, 90, 180)
                }
            }
        } else {
            // 直角边框
            canvas.models['BoundingBox'] = {
                paths: {
                    lineTop: new makerjs.paths.Line([minX, maxY], [maxX, maxY]),
                    lineBottom: new makerjs.paths.Line([minX, minY], [maxX, minY]),
                    lineLeft: new makerjs.paths.Line([minX, maxY], [minX, minY]),
                    lineRight: new makerjs.paths.Line([maxX, maxY], [maxX, minY])
                }
            }
        }
    }

    return canvas
}